<script setup lang="ts">
const route = useRoute()
const nuxtApp = useNuxtApp()
const { activeHeadings, updateHeadings } = useScrollspy()

const apiBase = useApiBase()
const platformInDev = ref(false)

/**
 * Portadas: la global (`/`, `/en`) y las de país (`/mx`).
 *
 * Importa para las anclas del menú. Antes `isHome` era sólo `/`, así que desde
 * la portada mexicana «Funciones» mandaba a `/#funciones` —la portada
 * HONDUREÑA— en vez de bajar por la página en la que estás. Lo mismo le pasaba
 * a `/en`.
 */
const PORTADAS = ['/', '/en', '/mx']
const rutaActual = computed(() => route.path.replace(/\/+$/, '') || '/')
const enPortada = computed(() => PORTADAS.includes(rutaActual.value))
const ancla = (id: string) => (enPortada.value ? `#${id}` : `/#${id}`)

// En México el camino no es el alta con tarjeta sino pedir acceso: el plugin MX
// todavía no deja que un cliente se configure solo. Ver `pages/mx/index.vue`.
const esMexico = computed(() => rutaActual.value === '/mx')

onMounted(async () => {
  try {
    const res = await $fetch<any>(`${apiBase}/landing/auth-providers`)
    platformInDev.value = res.in_development || false
  } catch {}
})

const { t } = useI18n()
const localePath = useLocalePath()

const items = computed(() => [{
  label: t('nav.features'),
  to: ancla('funciones'),
  active: enPortada.value && activeHeadings.value.includes('funciones')
}, {
  label: t('nav.modules'),
  to: ancla('modulos'),
  active: enPortada.value && activeHeadings.value.includes('modulos')
}, {
  label: t('nav.plans'),
  to: ancla('planes'),
  active: enPortada.value && activeHeadings.value.includes('planes')
},
// La asesoría contable es de Asefiscohn, en Honduras: en la portada mexicana esa
// sección no existe y el enlace caería en el vacío.
...(esMexico.value ? [] : [{
  label: t('nav.advisory'),
  to: ancla('asesoria'),
  active: enPortada.value && activeHeadings.value.includes('asesoria')
}]), {
  label: t('nav.partners'),
  to: '/socios',
}])

nuxtApp.hooks.hookOnce('page:finish', () => {
  updateHeadings([
    document.querySelector('#funciones'),
    document.querySelector('#modulos'),
    document.querySelector('#asesoria')
  ].filter(Boolean) as Element[])
})
</script>

<template>
  <UHeader>
    <template #left>
      <NuxtLink to="/" class="flex items-center">
        <UColorModeImage
          light="/images/logos/factorix-negro.webp"
          dark="/images/logos/factorix-blanco.webp"
          alt="Factorix"
          class="h-7"
          width="71"
          height="28"
        />
      </NuxtLink>
    </template>

    <template #right>
      <UNavigationMenu :items="items" variant="link" class="hidden lg:block" />
      <LanguageSwitch class="hidden sm:block" />
      <!-- `localePath` para que el boton lleve a /signup o /en/signup segun
           el idioma; con la ruta fija, un visitante en ingles saltaba al alta en
           español y perdia el idioma a mitad del camino. -->
      <UButton v-if="esMexico" label="Pedir acceso" icon="i-lucide-rocket" to="/mx#acceso" color="primary" />
      <UButton v-else-if="!platformInDev" :label="t('nav.start')" icon="i-lucide-rocket" :to="localePath('/signup')" color="primary" />
      <UColorModeButton />
    </template>

    <template #body>
      <UNavigationMenu :items="items" orientation="vertical" class="-mx-2.5" />
      <UButton v-if="esMexico" class="mt-4" label="Pedir acceso" icon="i-lucide-rocket" to="/mx#acceso" color="primary" block />
      <UButton v-else-if="!platformInDev" class="mt-4" :label="t('nav.start')" icon="i-lucide-rocket" :to="localePath('/signup')" color="primary" block />
      <LanguageSwitch class="mt-4 sm:hidden" />
    </template>
  </UHeader>
</template>
